import { PaymentProviderType } from '@/types/composition-functions';
import { normalizeCode } from '@/utils/billingLabels';

/**
 * School-facing setup guides for payment providers, surfaced from the payment
 * settings UI (issue #240). The Monnify prose also lives in
 * docs/payments/monnify-setup.md — keep the two in sync when editing.
 *
 * Only providers with an entry here show a "Setup guide" link in the settings
 * status panel, so this can grow one provider at a time.
 */
export interface PaymentSetupGuideStep {
  /** Short imperative heading, e.g. "Copy your API Key and Secret Key". */
  title: string;
  /** One or two sentences of detail. */
  detail: string;
}

export interface PaymentSetupGuide {
  /** PaymentProviderType value this guide documents, e.g. "MONNIFY". */
  provider: PaymentProviderType;
  /** Dialog title, e.g. "Set up Monnify". */
  title: string;
  /** Short lead paragraph shown under the title. */
  intro: string;
  /** Ordered setup steps. */
  steps: PaymentSetupGuideStep[];
  /** Webhook path appended to the backend API origin (no host). */
  webhookPath: string;
  /** Guidance shown beneath the webhook URL. */
  webhookNote: string;
  /** Link to the provider's own dashboard. */
  dashboardUrl: string;
  /** Label for the dashboard link. */
  dashboardLabel: string;
}

export const PAYMENT_SETUP_GUIDES: Readonly<Record<string, PaymentSetupGuide>> = {
  [PaymentProviderType.Monnify]: {
    provider: PaymentProviderType.Monnify,
    title: 'Set up Monnify',
    intro:
      'Monnify needs three details from your Monnify dashboard: an API Key, a Secret Key and a '
      + 'Contract Code. On your live school site, use the keys from your live Monnify account — '
      + 'sandbox (test) keys only work on a Gradeverse test site.',
    steps: [
      {
        title: 'Create or sign in to your Monnify account',
        detail:
          'Sign in at app.monnify.com. Keys from sandbox.monnify.com are test keys: they do not '
          + 'work on your live school site, so payments would fail.',
      },
      {
        title: 'Copy your API Key and Secret Key',
        detail:
          'In the Monnify dashboard open Settings, then API Keys & Webhooks. Paste the API Key into '
          + 'the "API Key" field and the Secret Key into the "Secret Key" field on this page.',
      },
      {
        title: 'Copy your Contract Code',
        detail:
          'On the same API Keys & Webhooks page, copy your Contract Code into the "Contract Code" '
          + 'field. Monnify uses it to route settlements to your account.',
      },
      {
        title: 'Register your webhook URL',
        detail:
          'Still under Settings, then API Keys & Webhooks, set the Webhook URL so Monnify can '
          + 'confirm completed payments. Use the URL below.',
      },
      {
        title: 'Save, switch it on and make a small payment',
        detail:
          'Save the provider here and switch it on, then make a small real payment to check '
          + 'everything works. After paying you return to the payment-status screen, and the '
          + 'webhook confirms the payment in the background.',
      },
    ],
    webhookPath: '/api/webhook/payment/monnify',
    webhookNote:
      'Copy this address exactly. If it starts with https://your-backend-domain, ask Gradeverse support for the right one.',
    dashboardUrl: 'https://app.monnify.com',
    dashboardLabel: 'Open Monnify dashboard',
  },
  [PaymentProviderType.Paystack]: {
    provider: PaymentProviderType.Paystack,
    title: 'Set up Paystack',
    intro:
      'Paystack needs two keys from your Paystack dashboard: a Public Key and a Secret Key. '
      + 'Test keys (pk_test_ and sk_test_) let you try a payment without real money; live keys '
      + '(pk_live_ and sk_live_) collect real payments.',
    steps: [
      {
        title: 'Sign in to your Paystack dashboard',
        detail: 'Sign in at dashboard.paystack.com. Your business must be activated by Paystack before live keys work.',
      },
      {
        title: 'Copy your Public Key and Secret Key',
        detail:
          'Open Settings, then API Keys & Webhooks. Paste the Public Key into the "Public Key" field '
          + 'and the Secret Key into the "Secret Key" field on this page.',
      },
      {
        title: 'Register your webhook URL',
        detail:
          'On the same page, paste the URL below into the Webhook URL box (the live one for live '
          + 'keys, the test one for test keys) so Paystack can confirm completed payments.',
      },
      {
        title: 'Save, switch it on and make a payment',
        detail:
          'Save the provider here and switch it on, then make a payment. With test keys no real '
          + 'money moves; switch to live keys before students start paying.',
      },
    ],
    webhookPath: '/api/webhook/payment/paystack',
    webhookNote:
      'Copy this address exactly. If it starts with https://your-backend-domain, ask Gradeverse support for the right one.',
    dashboardUrl: 'https://dashboard.paystack.com',
    dashboardLabel: 'Open Paystack dashboard',
  },
};

/** The setup guide for a provider name (e.g. "MONNIFY", any casing), or undefined if none exists. */
export function setupGuideFor(providerName: string | null | undefined): PaymentSetupGuide | undefined {
  const key = normalizeCode(providerName);
  if (!key) return undefined;
  return Object.values(PAYMENT_SETUP_GUIDES).find((guide) => normalizeCode(guide.provider) === key);
}

/** Placeholder host used when the backend origin cannot be resolved. */
export const WEBHOOK_HOST_PLACEHOLDER = 'https://your-backend-domain';

/**
 * Resolve the backend API origin from VITE_BACKEND_BASE_URL (the GraphQL
 * endpoint), falling back to a placeholder when it is unset or unparseable so
 * the dialog never renders a broken URL.
 */
export function resolveBackendOrigin(): string {
  const base = import.meta.env.VITE_BACKEND_BASE_URL as string | undefined;
  if (!base) return WEBHOOK_HOST_PLACEHOLDER;
  try {
    return new URL(base).origin;
  } catch {
    return WEBHOOK_HOST_PLACEHOLDER;
  }
}

/** Full webhook URL a school registers with the provider. */
export function webhookUrlFor(guide: PaymentSetupGuide): string {
  return `${resolveBackendOrigin()}${guide.webhookPath}`;
}
